import React, {Fragment, useCallback, useEffect, useMemo, useState} from "react";
import {compose} from "redux";
import {translate} from "react-i18next";
import {GUTTER_MD, GUTTER_SM, MODAL_WIDTH_LG} from "../../../../styles/constants";
import {Button, Col, Modal, Row, Steps} from "antd";
import EnhancedModal from "../../../../components/enhanced-modal";
import DatasetSelection from "./steps/DatasetSelection";
import MappingSetDefinition from "./steps/MappingSetDefinition";
import MappingSetName from "./steps/MappingSetName";
import {connect} from "react-redux";
import {
    resetFieldsMappingSet,
    hideWizard,
    saveMappingSetOnServer,
    getMappingSetObject,
    selectConnectionMappingSet,
    selectDatasetMappingSet,
    fetchDsdInfoMappingSet, forceTreeRefreshMappingSet, clearValidateFormatStatusOnExitMappingSet
} from "./actions";
import Call from "../../../../hocs/call";
import {prepareMappingSetForSaving} from "./utils";
import MappingSetValidateMapping from "./components/validate-status-components/MappingSetValidateMapping";
import "./styles.css";

const mapStateToProps = (state) => ({
    selectedConnection: state.scenes.mappingHandler.mappingSet.wizard.selectedConnection,
    selectedDataset: state.scenes.mappingHandler.mappingSet.wizard.selectedDataset,
    selectedMappingSet: state.scenes.mappingHandler.mappingSet.wizard.selectedMappingSet,
    datasets: state.scenes.mappingHandler.mappingSet.wizard.datasets,
    connections: state.scenes.mappingHandler.mappingSet.wizard.connections,
    mappingSetId: state.scenes.mappingHandler.mappingSet.index.mappingSetId,
    name: state.scenes.mappingHandler.mappingSet.wizard.name,
    dsd: state.scenes.mappingHandler.mappingSet.wizard.dsd,
    description: state.scenes.mappingHandler.mappingSet.wizard.description,
    isWizardVisible: state.scenes.mappingHandler.mappingSet.index.isWizardVisible,
    dataflow: state.scenes.mappingHandler.mappingSet.index.dataflow,
    dataflows: state.scenes.mappingHandler.mappingSet.tree.dataflows,
    mappingSets: state.scenes.mappingHandler.mappingSet.tree.mappingSets,
    stash: state.scenes.mappingHandler.mappingSet.wizard.stash,
    validateStatusOnExit: state.scenes.mappingHandler.mappingSet.wizard.validateStatusOnExit,
});

const mapDispatchToProps = (dispatch) => {
    return {
        onWizardHide: () => dispatch(hideWizard()),
        resetFields: (fields) => dispatch(resetFieldsMappingSet(fields)),
        saveMappingSet: (mappingSetData) => dispatch(saveMappingSetOnServer(mappingSetData)),
        getMappingSet: (mappingSetId) => dispatch(getMappingSetObject(mappingSetId)),
        onSelectConnection: (selectedConnection) => dispatch(selectConnectionMappingSet(selectedConnection)),
        onSelectDataset: (selectedDataset) => dispatch(selectDatasetMappingSet(selectedDataset)),
        fetchDsdInfo: ({urn}) => dispatch(fetchDsdInfoMappingSet(urn)),
        forceTreeRefresh: () => dispatch(forceTreeRefreshMappingSet()),
        clearValidate: () => dispatch(clearValidateFormatStatusOnExitMappingSet()),
    }
}

const MappingSetWizard = ({
                              t,
                              isWizardVisible,
                              selectedConnection,
                              selectedDataset,
                              selectedMappingSet,
                              mappingSetId,
                              name,
                              stash,
                              dataflow,
                              description,
                              connections,
                              datasets,
                              onSelectConnection,
                              onSelectDataset,
                              resetFields,
                              onWizardHide,
                              saveMappingSet,
                              getMappingSet,
                              dataflows,
                              dsd,
                              fetchDsdInfo,
                              forceTreeRefresh,
                              validateStatusOnExit,
                              clearValidate,
                              mappingSets,
                          }) => {
    const [step, setStep] = useState(1);
    const hideModalCallback = useCallback(() => {
        setStep(1);
        resetFields();
        forceTreeRefresh();
        onWizardHide();
    }, [resetFields, setStep]);

    const selectedDataflowDsd = useMemo(() => {
        if (!dataflow || !dataflows) return null;
        const selection = dataflows.find(d => d.id === dataflow.id);
        if (!selection) return null;
        const link = selection.links.find(item => item.rel === "structure");
        if (!link) return null;
        return link.urn
    }, [dataflow, dataflows]);

    useEffect(() => {
        if (selectedMappingSet && connections && !selectedConnection) {
            var selectedConnectionId = connections.find(conn => parseInt(conn.entityId, 10) === parseInt(selectedMappingSet.idConnection, 10));
            if(selectedConnectionId !== undefined) {
                onSelectConnection(selectedConnectionId);
            } else {
                Modal.error({
                    title: t('scenes.mappingHandler.DDBConnection.modals.connectionError.title'),
                    content: t('scenes.mappingHandler.DDBConnection.modals.connectionError.content')
                });
                onWizardHide();
            }
        }

        if (selectedMappingSet && selectedMappingSet.datasetId && !selectedDataset) {
            onSelectDataset({id: selectedMappingSet.datasetId});
        }
    }, [selectedMappingSet, connections, selectedConnection, datasets, selectedDataset]);

    const stepDesctiption = useMemo(() => `Conn: ${selectedConnection ? selectedConnection.connectionName : "N/D"} - Dataset: ${selectedDataset ? selectedDataset.name : "N/D"}`, [selectedConnection, selectedDataset]);

    return (
        <Fragment>
            <EnhancedModal
                visible={isWizardVisible}
                onCancel={hideModalCallback}
                fullscreen
                title={selectedMappingSet && selectedMappingSet.entityId ? `${t("scenes.mappingHandler.MappingSet.wizard.titleEdit")} - ${selectedMappingSet.name}` : t("scenes.mappingHandler.MappingSet.wizard.titleCreate")}
                width={MODAL_WIDTH_LG}
                footer={<Row type="flex" justify="end" align="middle" gutter={GUTTER_SM}>
                    <Col>
                        <Button
                            onClick={hideModalCallback}>{t('commons.buttons.close.title')}
                        </Button>
                    </Col>
                    {step > 1 && (
                        <Col>
                            <Button
                                onClick={() => setStep(prevState => prevState - 1)}
                            >
                                {t('commons.buttons.goBack.title')}
                            </Button>
                        </Col>
                    )}
                    {step < 3 && (
                        <Col>
                            <Button
                                type="primary"
                                onClick={() => setStep(prevState => prevState + 1)}
                                disabled={!selectedDataset || !selectedConnection}
                            >
                                {t('commons.buttons.next.title')}
                            </Button>
                        </Col>
                    )}
                    {step === 3 && (
                        <Col>
                            <Button
                                type="primary"
                                onClick={() => {
                                    if (!name) {
                                        Modal.warning({
                                            title: t('commons.alerts.invalidForm.title'),
                                            content: t('commons.alerts.invalidForm.content')
                                        });
                                        return;
                                    }
                                    if ((!selectedMappingSet || !selectedMappingSet.entityId) && (mappingSets || []).map(item => item.name).includes(name)) {
                                        Modal.warning({
                                            title: t('commons.alerts.invalidForm.title'),
                                            content: t('scenes.mappingHandler.MappingSet.wizard.steps.second.mappingNeedsToBeSavedAlert.nameAlreadyExists')
                                        });
                                        return;
                                    }
                                    saveMappingSet(prepareMappingSetForSaving(name, description, selectedMappingSet, selectedDataset, dataflow, (stash || []).filter(row => !row.entityId)));
                                    hideModalCallback();
                                }}
                                disabled={!selectedDataset || !selectedConnection}
                            >
                                {t('commons.buttons.save.title')}
                            </Button>
                        </Col>
                    )}
                </Row>}
            >
                <Steps current={step} size="small" style={{marginBottom: GUTTER_MD}}>
                    <Steps.Step
                        className="mapping__set__wizard"
                        title={
                            <span>
                            {t('scenes.mappingHandler.MappingSet.wizard.steps.first.title')}
                        </span>
                        }
                        icon={
                            <Button
                                shape="circle"
                                type={step === 1 ? 'primary' : null}
                                size="small"
                                style={{pointerEvents: 'none'}}
                            >
                                1
                            </Button>
                        }
                        description={stepDesctiption}

                    />
                    <Steps.Step
                        title={
                            <span>
                            {t('scenes.mappingHandler.MappingSet.wizard.steps.second.title')}
                        </span>
                        }
                        icon={
                            <Button
                                shape="circle"
                                type={step === 2 ? 'primary' : null}
                                size="small"
                                style={{pointerEvents: 'none'}}
                            >
                                2
                            </Button>
                        }
                    />
                    <Steps.Step
                        title={
                            <span>
                            {t('scenes.mappingHandler.MappingSet.wizard.steps.third.title')}
                        </span>
                        }
                        icon={
                            <Button
                                shape="circle"
                                type={step === 3 ? 'primary' : null}
                                size="small"
                                style={{pointerEvents: 'none'}}
                            >
                                3
                            </Button>
                        }
                    />
                </Steps>
                <Call cb={fetchDsdInfo}
                      cbParam={{urn: selectedDataflowDsd}}
                      disabled={!!dsd || !selectedDataflowDsd}
                >
                    <Call cb={getMappingSet} cbParam={mappingSetId}
                          disabled={!!selectedMappingSet || !mappingSetId || !dsd}>
                        {step === 1 && <DatasetSelection/>}
                        {step === 2 && <MappingSetDefinition/>}
                        {step === 3 && <MappingSetName/>}
                    </Call>
                </Call>
            </EnhancedModal>
            <EnhancedModal
                visible={!!validateStatusOnExit && validateStatusOnExit.status !== "success"}
                onCancel={clearValidate}
                title={t('scenes.mappingHandler.MappingSet.wizard.steps.second.validationAlert.mappingTitle')}
                width={MODAL_WIDTH_LG}
                footer={<Button
                    onClick={clearValidate}>{t('commons.buttons.close.title')}
                </Button>}
            ><MappingSetValidateMapping validateStatusFromProp={validateStatusOnExit}/></EnhancedModal>
        </Fragment>
    )
}

export default compose(
    translate(),
    connect(mapStateToProps, mapDispatchToProps)
)(MappingSetWizard);
